// ---------------------------------------------------------------------------
// Round type chips. `L`, `N`, `C`, each with its own fill so a sequence reads as
// a pattern rather than as a sentence to be parsed letter by letter.
//
// The fills come from token plus alpha rather than from literals: the handoff's
// chip greys sit between `surface` and `raised` and have no token of their own,
// and a literal would not follow the light theme.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import type { RoundType } from '@countdown/shared';
import { roundLabel } from '../../lib/format.js';

export const ROUND_CHIP_LETTER: Record<RoundType, string> = {
  letters: 'L',
  numbers: 'N',
  conundrum: 'C',
};

export const ROUND_CHIP_FILL: Record<RoundType, string> = {
  letters: 'bg-blue-700/45 text-blue-200',
  numbers: 'bg-raised text-ink',
  conundrum: 'bg-accent/15 text-accent',
};

const ROUND_CHIP_EDGE: Record<RoundType, string> = {
  letters: 'border-blue-500/50',
  numbers: 'border-line',
  conundrum: 'border-accent/60',
};

export function RoundTypeChip(props: {
  type: RoundType;
  /** Position number shown above the letter, in the lobby's builder well. */
  position?: number;
  size?: 'sm' | 'md';
  onClick?: () => void;
}): ReactElement {
  const { type, position, size = 'sm', onClick } = props;
  const label = roundLabel(type);

  const content =
    position === undefined ? (
      <span className={`font-mono leading-none font-bold ${size === 'md' ? 'text-[15px]' : 'text-[13px]'}`}>
        {ROUND_CHIP_LETTER[type]}
      </span>
    ) : (
      <>
        {/* Two digits at most, centred and tabular, so round 9 becoming round 10
            does not nudge the letter under it. */}
        <span className="min-w-[2ch] text-center font-mono text-[9px] leading-none text-ink-dim tabular">
          {position}
        </span>
        <span className="font-sans text-[22px] leading-none font-extrabold">{ROUND_CHIP_LETTER[type]}</span>
      </>
    );

  const shell =
    position === undefined
      ? `inline-flex items-center justify-center rounded-chip border px-2 py-1.5 ${size === 'md' ? 'min-w-9' : 'min-w-7'}`
      : 'flex h-14 w-11 flex-col items-center justify-center gap-[3px] rounded-[9px] border';

  // Selecting the letter out of a chip you are trying to drag is never what you
  // meant, and the fill changes when a round changes type.
  const className = `${shell} ${ROUND_CHIP_FILL[type]} ${ROUND_CHIP_EDGE[type]} select-none transition-[background-color,border-color,color] duration-150 ease-out`;

  if (!onClick) {
    return (
      <div className={className} data-round-type={type} title={label}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      // A tappable chip earns a 44px target even where a static one is smaller.
      className={`${className} ${position === undefined ? 'min-h-11 px-3' : ''}`}
      data-round-type={type}
      aria-label={label}
    >
      {content}
    </button>
  );
}
